/* eslint-disable react-hooks/exhaustive-deps */

"use client";

import { Lookup, SpringRef } from "@react-spring/web";
import { Suspense, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { isApiError } from "@/app/common-types/api-error.types";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import CoverTypeContainer from "@/components/molecules/questions-container/cover-type/cover-type-container";
import CruiseContainer from "@/components/molecules/questions-container/cruise/cruise-container";
import LocationToContainer from "@/components/molecules/questions-container/location-to/location-to-container";
import OrganiserContainer from "@/components/molecules/questions-container/organizer/organizer-container";
import PartyTypeContainer from "@/components/molecules/questions-container/party-type/party-type-container";
import TravelDateContainer from "@/components/molecules/questions-container/travel-date/travel-date-container";
import TravellerContainer from "@/components/molecules/questions-container/traveller/traveller-container";
import { useQuestionsState } from "@/providers/questions-state-provider";
import { RootState } from "@/store/root-reducer";
import { STCountryArray } from "@/types/QuestionPayloadType";
import { QuotePayload } from "@/types/quote/quote-data";
import { Traveller } from "@/types/Traveller";
import { findIdbyAnswer } from "@/utils/cover-type.utils";
import { findAnswerQThreeByValue } from "@/utils/cruise.utils";
import { updateAnswerLabels } from "@/utils/party-type.utils";
import { getSubmitButtonState } from "@/utils/question-location-to.utils";
import { CoverTypeContent } from "@/utils/schema/cover-type-page-schema.utils";
import { CruiseContent } from "@/utils/schema/cruise-page-schema.utils";
import { LocationToContent } from "@/utils/schema/location-to-page-schema.utils";
import { OrganiserContent } from "@/utils/schema/organiser-page-schema.utils";
import { PartyTypeContent } from "@/utils/schema/party-type-page-schema.utils";
import { DateTypeContent } from "@/utils/schema/travel-date-page-schema.utils";
import { TravellersContent } from "@/utils/schema/travellers-page-schema.utils";

export type CommonPageContent =
  | (LocationToContent & { page: "travelling-to" })
  | (CoverTypeContent & { page: "cover-type" })
  | (CruiseContent & { page: "cruise" })
  | (DateTypeContent & { page: "travel-dates" })
  | (PartyTypeContent & { page: "cover-for" })
  | (OrganiserContent & { page: "organiser" })
  | (TravellersContent & { page: "traveller" });

export default function QuestionsContainer({
  pageContent,
  formRef,
  ctaRef,
}: Readonly<{
  showondesktop?: string;
  pageContent: CommonPageContent;
  formRef?: SpringRef<Lookup>;
  ctaRef?: SpringRef<Lookup>;
}>) {
  const { current } = useWizard();
  const { selected, setSelectedState } = useQuestionsState();
  const quoteResponse = useSelector((state: RootState) => state.quote);
  const { loading } = quoteResponse;
  const modifiedQuoteResponse = useMemo(
    () =>
      (isApiError(quoteResponse)
        ? quoteResponse.response?.data
        : quoteResponse) as QuotePayload,
    [quoteResponse]
  );

  const [toLocationCountryDetails, setToLocationCountryDetails] = useState<
    STCountryArray | undefined
  >(undefined);
  const [policyType, setPolicyType] = useState<string | undefined>(undefined);
  const [toCountry, setToCountry] = useState<string | undefined>(undefined);

  const [selectedAnswer, setSelectedAnswer] = useState(0);
  const [travellerDetails, setTravellerDetails] = useState<Traveller[]>([]);

  // for common things
  const handleCommonStates = (quoteResponseData: QuotePayload | null) => {
    const selectedPolicyType =
      quoteResponseData?.attributes?.tripDetails?.policyType ?? "";

    setPolicyType(selectedPolicyType);
    if (selectedPolicyType === "ANNUAL_MULTI_TRIP") {
      const toLocation = quoteResponseData?.attributes?.tripDetails?.area;
      if (toLocation) setToCountry(toLocation);
    } else {
      const toLocation = quoteResponseData?.attributes?.tripDetails?.toLocation;
      if (toLocation)
        setToLocationCountryDetails(
          quoteResponseData?.attributes?.tripDetails?.toLocationCountryDetails
        );
      setToCountry(toLocation ?? undefined);
    }

    if (quoteResponseData?.attributes?.travellerDetails?.traveller) {
      setTravellerDetails(
        quoteResponseData?.attributes?.travellerDetails?.traveller
      );
    }
  };

  // for change selected state

  const handleSelected = (quoteResponseData: QuotePayload | null) => {
    if (quoteResponse) {
      if (current.component === "question/cover-type") {
        const selectedType =
          quoteResponseData?.attributes?.tripDetails?.policyType;
        let policyId: number | undefined = 0;
        if (selectedType) {
          policyId = findIdbyAnswer(selectedType)?.id;
        }
        setSelectedState(policyId ?? 0);
      }

      if (current.component === "question/travelling-to") {
        const selectedPolicyType =
          quoteResponseData?.attributes?.tripDetails?.policyType ?? "";
        if (selectedPolicyType === "ANNUAL_MULTI_TRIP") {
          const toLocation = quoteResponseData?.attributes?.tripDetails?.area;
          if (toLocation) setSelectedState(toLocation);
        } else {
          const toLocation =
            quoteResponseData?.attributes?.tripDetails?.toLocation;
          if (toLocation) setSelectedState(toLocation);
          setToLocationCountryDetails(
            quoteResponseData?.attributes?.tripDetails?.toLocationCountryDetails
          );
        }
      }

      if (current.component === "question/cruise") {
        const goingOnCruise =
          quoteResponseData?.attributes?.tripDetails?.goingOnCruise;
        const cruiseId = findAnswerQThreeByValue(goingOnCruise)?.id;
        setSelectedState(cruiseId ?? 0);
      }

      if (current.component === "question/travel-dates") {
        const startDateSaved: string | undefined =
          quoteResponseData?.attributes?.tripDetails?.startDate;

        if (startDateSaved) {
          setSelectedState(startDateSaved);
        }
      }

      if (current.component === "question/cover-for") {
        const partyType = quoteResponseData?.attributes?.tripDetails?.partyType;
        setSelectedState(partyType || "");
      }

      if (current.component === "question/organiser") {
        setSelectedState(
          quoteResponseData?.attributes?.organiser?.travelling || ""
        );
      }
    }
  };

  useEffect(() => {
    handleCommonStates(quoteResponse);
    handleSelected(quoteResponse);
  }, [quoteResponse]);

  const setAnswerNo = (answerNo: number) => {
    setSelectedAnswer(answerNo);
  };

  // Conditionally render either LocationToContainer or CoverTypeContainer based on the route
  const renderCurrentQuestion = () => {
    if (
      current.component === "question/travelling-to" &&
      pageContent.page === "travelling-to"
    ) {
      return (
        <LocationToContainer
          tripDetails={modifiedQuoteResponse?.attributes?.tripDetails ?? {}}
          usagePage={current.id.replace(/^\//, "")}
          pageContent={{
            ...pageContent,
            cancelEditingBtnTextEurope: "Cancel editing",
            cancelEditingBtnTextWorldWide: "Cancel editing",
            finishEditingBtnText: "Finish editing",
          }}
          stateFromLocation={
            modifiedQuoteResponse?.attributes?.tripDetails?.fromLocation ?? ""
          }
          toLocationCountryDetails={toLocationCountryDetails}
          selectedpolicyType={policyType}
          buttonDisable={getSubmitButtonState(
            policyType ?? "",
            (selected as string) || "",
            false
          )}
          setToLocationCountryDetails={setToLocationCountryDetails}
          quoteResponse={modifiedQuoteResponse}
          quoteLoading={loading}
          formRef={formRef}
          ctaRef={ctaRef}
        />
      );
    }
    if (
      current.component === "question/cover-type" &&
      pageContent.page === "cover-type"
    ) {
      return (
        <CoverTypeContainer
          quoteLoading={loading}
          formRef={formRef}
          ctaRef={ctaRef}
          usagePage={current.id.replace(/^\//, "")}
          pageContent={pageContent}
        />
      );
    }
    if (
      current.component === "question/cruise" &&
      pageContent.page === "cruise"
    ) {
      return (
        <CruiseContainer
          buttonDisable={selected === 0}
          usagePage={current.id.replace(/^\//, "")}
          quoteLoading={loading}
          formRef={formRef}
          ctaRef={ctaRef}
          pageContent={pageContent}
        />
      );
    }

    if (
      current.component === "question/travel-dates" &&
      pageContent.page === "travel-dates"
    ) {
      return (
        <TravelDateContainer
          pageContent={pageContent}
          usagePage={current.id.replace(/^\//, "")}
          policyType={policyType || ""}
          fromCountry={
            modifiedQuoteResponse?.attributes?.tripDetails?.fromLocation
          }
          toCountry={toCountry}
          tripStartDate={
            modifiedQuoteResponse?.attributes?.tripDetails?.startDate ??
            undefined
          }
          tripEndDate={
            modifiedQuoteResponse?.attributes?.tripDetails?.endDate ?? undefined
          }
          quoteLoading={loading}
          ctaRef={ctaRef}
          formRef={formRef}
        />
      );
    }

    if (
      current.component === "question/cover-for" &&
      pageContent.page === "cover-for"
    ) {
      const qFiveAnwerArray = updateAnswerLabels(pageContent);
      return (
        <PartyTypeContainer
          answerArray={qFiveAnwerArray as []}
          pageContent={pageContent}
          buttonDisable={!selected}
          usagePage={current.id.replace(/^\//, "")}
          selectedParty={selected as string}
          quoteLoading={loading}
          ctaRef={ctaRef}
          formRef={formRef}
        />
      );
    }

    if (
      current.component === "question/organiser" &&
      pageContent.page === "organiser"
    ) {
      return (
        <OrganiserContainer
          travellerType={
            quoteResponse?.attributes?.tripDetails?.partyType ?? "SINGLE"
          }
          usagePage={current.id.replace(/^\//, "")}
          addressBlockButton={undefined}
          quoteLoading={loading}
          pageContent={pageContent}
          ctaRef={ctaRef}
          formRef={formRef}
        />
      );
    }

    if (
      current.component === "question/travellers" &&
      pageContent.page === "traveller"
    ) {
      return (
        <TravellerContainer
          pageContent={pageContent}
          travellerType={
            quoteResponse?.attributes?.tripDetails?.partyType ?? "SINGLE"
          }
          setSelectedAnswer={setAnswerNo}
          buttonDisable={selectedAnswer === 0}
          usagePage={current.id.replace(/^\//, "")}
          travellerDetailsEdit={travellerDetails}
          medicalTermsAndConditions={
            quoteResponse?.attributes?.travellerDetails
              ?.medicalTermsAndConditions || undefined
          }
          quoteLoading={loading}
          formRef={formRef}
          ctaRef={ctaRef}
        />
      );
    }

    return null;
  };

  return <Suspense>{renderCurrentQuestion()}</Suspense>;
}
